import React, { useRef } from 'react';
import { useHover } from 'usehooks-ts';
import classNames from 'classnames';
import type { HotelCardProps } from './types';
import HotelCardHeaderLabel from './components/HotelCardHeaderLabel';
import HotelCardTitleBlock from './components/HotelCardTitleBlock';
import HotelCardFeatures from './components/HotelCardFeatures';
import HotelCardLabels from './components/HotelCardLabels';
import HotelCardPriceBlock from './components/HotelCardPriceBlock';
import HotelCardAdjacentTours from './components/HotelCardAdjacentTours';
import HotelCardGalleryOverlay from './components/HotelCardGalleryOverlay';
import HotelCardPenalizedFilters from './components/HotelCardPenalizedFilters';
import useLongHover from './hooks/useLongHover';
import { HOTEL_CARD_TEST_ID } from './constants';
import styles from './hotelCard.module.css';

const HotelCard = (props: HotelCardProps): JSX.Element => {
  const {
    id,
    images,
    bonusCount,
    currencyCode,
    hotelPageVisited,
    stars,
    hotelName,
    hotelLocation,
    rating,
    reviewsCount,
    features,
    instantConfirm,
    isFlexDates,
    price,
    innerLabels,
    headerLabel,
    isHotelOnly,
    availability,
    adjacentTours = [],
    hotelPageHref,
    openInCurrentTab,
    datesInfo,
    penalizedFilters = [],
    onLongHover,
    className,
    cashbackLabel,
    cashbackIconWebp,
    cashbackIconPng,
    cashbackTooltipText,
    labelColor,
    textColor,
  } = props;

  const cardRef = useRef<HTMLDivElement>(null);
  const isCardHovered = useHover(cardRef);

  useLongHover(isCardHovered, onLongHover);

  return (
    <div
      ref={cardRef}
      data-testid={HOTEL_CARD_TEST_ID}
      className={classNames(styles.cardContainer, className)}
    >
      <HotelCardHeaderLabel headerLabel={headerLabel} />
      <div className={styles.cardContent}>
        <div className={styles.galleryContainer}>
          <HotelCardGalleryOverlay
            id={id}
            images={images}
            price={price}
            isMobile={false}
            bonusCount={bonusCount}
            hotelPageVisited={hotelPageVisited}
            stars={stars}
            hotelName={hotelName}
            hotelLocation={hotelLocation}
            rating={rating}
            reviewsCount={reviewsCount}
            hotelPageHref={hotelPageHref}
            openInCurrentTab={openInCurrentTab}
            cashbackTooltipText={cashbackTooltipText}
            labelColor={labelColor}
            textColor={textColor}
            cashbackLabel={cashbackLabel}
            cashbackIconWebp={cashbackIconWebp}
            cashbackIconPng={cashbackIconPng}
          />
        </div>

        <div className={styles.hotelInfo}>
          <div
            className={classNames({ [styles.topPart]: !innerLabels.length })}
          >
            <HotelCardTitleBlock
              id={id}
              price={price}
              stars={stars}
              hotelName={hotelName}
              hotelLocation={hotelLocation}
              hotelPageHref={hotelPageHref}
              openInCurrentTab={openInCurrentTab}
            />
            <HotelCardFeatures isMobile={false} features={features} />
            <HotelCardLabels isMobile={false} labels={innerLabels} />
          </div>
          <div>
            <HotelCardPriceBlock
              instantConfirm={instantConfirm}
              isFlexDates={isFlexDates}
              isMapCard={false}
              price={price}
              currencyCode={currencyCode}
              availability={availability}
              isHotelOnly={isHotelOnly}
              isCardHovered={isCardHovered}
              hotelPageHref={hotelPageHref}
              openInCurrentTab={openInCurrentTab}
              datesInfo={datesInfo}
            />
            <HotelCardAdjacentTours
              isFlexDates={isFlexDates}
              isMobile={false}
              isHotelOnly={isHotelOnly}
              adjacentTours={adjacentTours}
              currencyCode={currencyCode}
            />
            <HotelCardPenalizedFilters
              isMobile={false}
              penalizedFilters={penalizedFilters}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotelCard;
